import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

const STUDENT_PROFILE_KEY = 'eduwrite_student_profile';
export const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface StudentProfileCache {
    studentId: string;
    studentName: string;
    classId: string;
    serverUrl: string;
    orgId: string | null;
    groupId: string | null; // For collective/table identity
    keyStage: string | null;
    deviceToken?: string | null;
    cachedAt: number;
}

interface AuthState {
    classId: string | null;
    deviceToken: string | null; // JWT for device auth
    serverUrl: string | null;
    studentName: string | null;
    studentId: string | null; // DB UUID
    deviceId: string | null;
    orgId: string | null;
    groupId: string | null; // For collective/table identity
    keyStage: string | null;
    isReady: boolean;
    cachedProfile: StudentProfileCache | null;
    cohortIds: string[]; // WP-075: Cohort memberships for cross-class grouping
    classFeedbackMode: boolean; // WP-100: Classroom feedback mode flag

    joinClass: (classId: string, studentName: string, studentId?: string | null, serverUrl?: string, keyStage?: string | null) => void;
    joinAsGroup: (classId: string, groupId: string, groupName: string, serverUrl?: string) => void;
    leaveClass: () => void;
    setReady: (isReady: boolean) => void;
    setDeviceToken: (token: string) => void;
    switchStudent: () => void;
    setCohortIds: (cohortIds: string[]) => void;
    setClassFeedbackMode: (enabled: boolean) => void;

    // Caching methods
    cacheStudentProfile: (profile: StudentProfileCache) => Promise<void>;
    loadCachedProfile: (classId?: string) => Promise<StudentProfileCache | null>;
    clearCachedProfile: () => Promise<void>;
    hydrateFromCache: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set) => ({
    classId: null,
    serverUrl: null,
    studentName: null,
    studentId: null,
    deviceId: 'APP-DEVICE-ID-UNSET',
    orgId: null,
    groupId: null,
    keyStage: null,
    deviceToken: null,
    isReady: false,
    cachedProfile: null,
    cohortIds: [],
    classFeedbackMode: false,

    joinClass: (classId, studentName, studentId, serverUrl, keyStage) => set({
        classId,
        studentName,
        studentId: studentId || null,
        serverUrl: serverUrl || null,
        keyStage: keyStage || null,
        groupId: null, // Clear any group association when joining as individual
        isReady: false
    }),

    joinAsGroup: (classId, groupId, groupName, serverUrl) => set({
        classId,
        studentName: groupName, // Use group name as the display name
        studentId: null, // No individual student ID when joining as group
        groupId,
        serverUrl: serverUrl || null,
        isReady: false
    }),

    leaveClass: () => set({
        deviceToken: null,
        classId: null,
        serverUrl: null,
        studentName: null,
        studentId: null,
        orgId: null,
        groupId: null,
        keyStage: null,
        isReady: false,
        cachedProfile: null,
        cohortIds: []
    }),

    setReady: (isReady) => set({ isReady }),
    setDeviceToken: (token) => set({ deviceToken: token }),
    setCohortIds: (cohortIds) => set({ cohortIds }),
    setClassFeedbackMode: (classFeedbackMode) => set({ classFeedbackMode }),

    switchStudent: () => set({ studentName: null, studentId: null, groupId: null, isReady: false, cohortIds: [] }),

    // Cache the student profile to AsyncStorage for quick re-login
    cacheStudentProfile: async (profile) => {
        try {
            await AsyncStorage.setItem(STUDENT_PROFILE_KEY, JSON.stringify(profile));
            set({ cachedProfile: profile });
        } catch (e) {
            console.error('[AuthStore] Failed to cache profile:', e);
        }
    },

    // Load cached profile from AsyncStorage with TTL validation
    loadCachedProfile: async (classId?: string) => {
        try {
            const raw = await AsyncStorage.getItem(STUDENT_PROFILE_KEY);
            if (!raw) return null;

            const profile: StudentProfileCache = JSON.parse(raw);
            const now = Date.now();

            // Check TTL
            if (now - profile.cachedAt > CACHE_TTL_MS) {
                await AsyncStorage.removeItem(STUDENT_PROFILE_KEY);
                return null;
            }

            // If classId provided, verify match
            if (classId && profile.classId !== classId) {
                return null;
            }

            return profile;
        } catch (e) {
            console.error('[AuthStore] Failed to load cached profile:', e);
            return null;
        }
    },

    // Clear cached profile from AsyncStorage and store
    clearCachedProfile: async () => {
        try {
            await AsyncStorage.removeItem(STUDENT_PROFILE_KEY);
            set({ cachedProfile: null });
        } catch (e) {
            console.error('[AuthStore] Failed to clear cached profile:', e);
        }
    },

    // Hydrate store from cached profile on app launch
    hydrateFromCache: async () => {
        const profile = await useAuthStore.getState().loadCachedProfile();
        if (!profile) return false;

        set({
            deviceToken: profile.deviceToken || null,
            studentId: profile.studentId,
            studentName: profile.studentName,
            classId: profile.classId,
            serverUrl: profile.serverUrl,
            orgId: profile.orgId,
            groupId: profile.groupId || null,
            keyStage: profile.keyStage || null,
            cachedProfile: profile,
            isReady: false
        });
        return true;
    }
}));
